import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog, DialogContent } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/NewPcButton';
import { ROUTES } from '@/routes';

import { useMembershipPreviewMode } from './MembershipContentLock';

export type MembershipReviewPreviewTutorialAction =
  | 'generateAnswer'
  | 'selectTemplate'
  | 'submitAnswer'
  | 'editTemplate';

type TutorialCopy = {
  title: string;
  description: string;
  result: string;
};

type MembershipReviewPreviewTutorialContextValue = {
  openTutorial: (action: MembershipReviewPreviewTutorialAction) => boolean;
};

const tutorialCopyByAction = {
  generateAnswer: {
    title: 'AI 답변 생성 체험',
    description: '리뷰 내용을 바탕으로 바로 사용할 수 있는 답변 후보를 생성하는 흐름이에요.',
    result: '구독 후에는 AI가 추천한 답변을 선택해 리뷰 답글 입력란에 바로 반영할 수 있어요.',
  },
  selectTemplate: {
    title: '답변 템플릿 선택 체험',
    description: '자주 쓰는 답변 템플릿을 골라 리뷰 답글 입력란에 적용하는 흐름이에요.',
    result: '구독 후에는 저장된 템플릿을 선택해 반복 답변 시간을 줄일 수 있어요.',
  },
  submitAnswer: {
    title: '리뷰 답변 등록 체험',
    description: '작성한 리뷰 답변을 마켓에 등록하는 업무 흐름이에요.',
    result: '구독 후에는 선택한 마켓 정책에 맞춰 실제 리뷰 답변을 등록할 수 있어요.',
  },
  editTemplate: {
    title: '답변 템플릿 편집 체험',
    description: '리뷰 답변에 자주 쓰는 문구를 템플릿으로 추가하거나 수정하는 흐름이에요.',
    result: '구독 후에는 템플릿을 직접 관리하고 리뷰 답변 작성에 재사용할 수 있어요.',
  },
} satisfies Record<MembershipReviewPreviewTutorialAction, TutorialCopy>;

const MembershipReviewPreviewTutorialContext =
  createContext<MembershipReviewPreviewTutorialContextValue | null>(null);

export const MembershipReviewPreviewTutorialProvider = ({ children }: PropsWithChildren) => {
  const isPreviewMode = useMembershipPreviewMode();
  const navigate = useNavigate();
  const [activeAction, setActiveAction] = useState<MembershipReviewPreviewTutorialAction | null>(
    null,
  );

  const closeTutorial = () => setActiveAction(null);

  const openTutorial = useCallback(
    (action: MembershipReviewPreviewTutorialAction) => {
      if (!isPreviewMode) return false;

      setActiveAction(action);
      return true;
    },
    [isPreviewMode],
  );

  const contextValue = useMemo(() => ({ openTutorial }), [openTutorial]);
  const activeCopy = activeAction ? tutorialCopyByAction[activeAction] : null;

  return (
    <MembershipReviewPreviewTutorialContext.Provider value={contextValue}>
      {children}
      <Dialog open={Boolean(activeCopy)} onOpenChange={(open) => !open && closeTutorial()}>
        <DialogContent
          noCloseBtn
          className="w-[500px] max-w-[calc(100vw-32px)] p-[30px]"
          data-membership-preview-interactive="true"
        >
          {activeCopy && (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <span className="text-point-500 Caption1M12">리뷰 답변 체험</span>
                <div className="text-gr-900 Title3S18">{activeCopy.title}</div>
                <div className="text-gr-700 Body5M14">{activeCopy.description}</div>
              </div>
              <div className="rounded-[8px] bg-gr-50 p-4 text-gr-800 Body5M14">
                {activeCopy.result}
              </div>
              <div className="flex gap-3">
                <Button className="h-[44px] flex-1" variant="secondary" onClick={closeTutorial}>
                  계속 둘러보기
                </Button>
                <Button
                  className="h-[44px] flex-1"
                  variant="solid"
                  onClick={() => navigate(ROUTES.moreMembership)}
                >
                  구독하고 사용하기
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MembershipReviewPreviewTutorialContext.Provider>
  );
};

export const useMembershipReviewPreviewTutorial = () => {
  const context = useContext(MembershipReviewPreviewTutorialContext);
  if (!context) {
    throw new Error(
      'useMembershipReviewPreviewTutorial must be used within MembershipReviewPreviewTutorialProvider',
    );
  }

  return context;
};
